import React, { useContext } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import styles from "../signature.module.css";
import RandomCard from "../../../components/RandomCard/RandomCard";
import { FaFileSignature } from "react-icons/fa";
import {
  alternatives,
  docusignData,
  docusignList,
  alternativeConclusion,
} from "../../../constants/signature";
import { BASE_ASSETS_URL } from "../../../constants/constants";
import AppContext from "../../../components/AppContext";
import StickyLoginCard from "components/StickyLoginCard";

const ListComponent = ({ title, list }) => {
  return (
    <div className="text-gray-900">
      {title && (
        <div className="text-xl font-bold text-gray-900 mb-3">{title}:</div>
      )}
      <ul className="ml-10 list-disc text-lg">
        {list.map((item, index) => (
          <li key={index} className="">
            <span className="">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ParaGraphs = ({
  heading1,
  heading2,
  image,
  imageCaption,
  altText,
  paraList,
  list,
}) => {
  return (
    <div className="flex flex-col gap-2">
      {heading1 && (
        <div className="font-bold text-3xl text-black mb-4">{heading1}</div>
      )}
      {heading2 && (
        <div className="font-bold text-xl text-gray-900">{heading2}</div>
      )}
      {image && (
        <figure className="relative w-full mb-5 sm:mb-6 flex flex-col gap-2">
          <Image
            width="10"
            height="10"
            className="w-full h-auto object-cover border"
            src={`${BASE_ASSETS_URL}/v2/alternatives/${image}`}
            alt={altText}
          />
          <figcaption className="text-center px-6 sm:px-10 text-xs sm:text-sm italic">
            {imageCaption}
          </figcaption>
        </figure>
      )}
      <ul className="text-gray-900 flex flex-col gap-4 list-disc text-lg">
        {paraList?.map((paragraph, index) =>
          typeof paragraph == "object" ? (
            paragraph.map((para, index) => (
              <li key={index} className="text-black font-bold ml-10">
                {para.li_head || para.ol_head}{" "}
                <span className="font-normal text-gray-900">
                  {para.li_desc || para.ol_desc}
                </span>
              </li>
            ))
          ) : (
            <p key={index} className="text-gray-900">
              {paragraph}
            </p>
          )
        )}
        {list && <ListComponent title="" list={list} />}
      </ul>
    </div>
  );
};

const ComparisonTable = ({ tableData }) => {
  return (
    <div className="w-full overflow-x-scroll sm:overflow-x-auto">
      <h2 className="mt-8 mb-6 text-gray-900">{tableData?.heading2}</h2>
      <div className="w-[500px] sm:w-full bg-white">
        <thead>
          <tr>
            {tableData.headers.map((header, index) => (
              <th
                key={index}
                className="py-2 px-4 bg-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.rows.map((row, index) => (
            <tr key={index} className="text-sm">
              <td className="py-2 px-4 border-b border-r border-gray-200">
                {row.feature}
              </td>
              <td className="py-2 px-4 border-b border-r border-gray-200">
                {row.boloSign}
              </td>
              <td className="py-2 px-4 border-b border-r border-gray-200">
                {row.docuSign}
              </td>
              <td className="py-2 px-4 border-b border-r border-gray-200">
                {row.otherCompetitors}
              </td>
            </tr>
          ))}
        </tbody>
      </div>
    </div>
  );
};

const DocusignCompetitors = () => {
  const context = useContext(AppContext);
  return (
    <div className="w-full md:mt-20 mt-8">
      <Head>
        <meta charSet="utf-8" />
        <title>
          Top 10 DocuSign Alternatives in 2024: Best eSignature Solutions for
          Small Businesses
        </title>
        <meta
          name="description"
          content="Discover the best DocuSign alternatives in 2024, including BoloSign, offering affordable and feature-rich eSignature solutions for small businesses."
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="Boloforms" />
        <meta name="publisher" content="BoloForms" />
        <meta
          name="keywords"
          content="DocuSign Competitors, DocuSign Alternatives, DocuSign Competitors 2022, DocuSign Competitors 2023"
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/alternatives/docusign-alternatives/"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Top 10 DocuSign Alternatives in 2024: Best eSignature Solutions for Small Businesses"
        />
        <meta name="twitter:site" content="@boloforms" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/signature/alternatives/docusign-alternatives/"
        />
        <meta
          name="twitter:title"
          content="Top 10 DocuSign Alternatives in 2024: Best eSignature Solutions for Small Businesses"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@boloforms" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta name="twitter:description" content="" />
        <meta property="og:description" content="" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/alternatives/docusign-alternatives/"
        />
      </Head>

      <div className="max-w-screen-xl md:px-8 px-4 mx-auto mb-20">
        <div className="mx-auto">
          <div className="relative w-full h-fit sm:h-[400px] lg:h-[500px]">
            <Image
              className="w-full h-full sm:h-[400px] lg:h-[500px] object-cover rounded-3xl rounded-b-3xl"
              src={require("@assets/signature/alternatives/alternatives-top.png")}
              width={10}
              height={10}
              alt=""
            />
            <div className="relative lg:absolute md:left-8 md:bottom-8 left-0 bottom-0 bg-white z-1000 py-8 lg:pl-10 lg:pr-24 rounded-3xl">
              <h2 className="mx-auto text-violet-600 font-semibold uppercase text-lg">
                APPS
              </h2>
              <h1 className="text-black text-4xl">
                Top 10 DocuSign Alternatives in 2024
              </h1>
              <div className="flex items-center gap-5 mt-6">
                <div className="w-[70px] h-[70px]">
                  <Image
                    src={`${BASE_ASSETS_URL}/about/paresh.png`}
                    width="10"
                    height="10"
                    className="w-full h-full object-cover rounded-full"
                    alt=""
                  />
                </div>
                <div className="">
                  <div className="font-bold text-xl text-black">
                    Paresh Deshmukh
                  </div>
                  <div className="text-gray-800">
                    Last updated on August 10, 2023
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row-reverse justify-between mt-[50px] sm:mt-[350px] lg:mt-[100px] lg:gap-12 xxl:gap-40">
          {/* <div className="hidden lg:block">
            <RandomCard
              icon={<FaFileSignature />}
              title="Create a free BoloForms account today!"
              description="Create your free account today and start creating your own digital signature."
              buttonText="Create free account"
              buttonUrl={`https://signature.boloforms.com/login?from=${
                context.globalData.from || "DOCUSIGN_ALTERNATIVE"
              }`}
            />
          </div> */}
          <div className="self-center lg:self-start lg:sticky lg:top-[140px] flex justify-center">
            <StickyLoginCard
              cardWidth="xs:w-[370px]"
              title={`Create a free BoloForms account today!`}
              description={`Create your free account today and start creating your own digital signature.`}
            />
          </div>
          <div className="flex-1">
            <div className="flex flex-col gap-10 mt-10 lg:mt-6 text-lg text-gray-900">
              <p className="">
                {`Are you looking for DocuSign alternatives that offer great
                features at a more affordable price? You're in the right place!
                In this comprehensive guide, we'll explore the top 10
                DocuSign competitors, with a special focus on BoloSign - an
                excellent DocuSign alternative that's perfect for small
                businesses.`}
              </p>
              {/* <ListComponent title="" list={docusignList} />
              <p className="">{`Let's`} get started.</p> */}
            </div>
            <div className="flex flex-col gap-6 mt-10">
              {docusignData.map((item, index) => (
                <ParaGraphs
                  key={index}
                  heading1={item?.heading1}
                  heading2={item?.heading2}
                  image={item?.image}
                  imageCaption={item?.imageCaption}
                  altText={item?.altText}
                  paraList={item?.paragraphs}
                  list={item?.listData}
                />
              ))}
            </div>
            <div className="w-full flex flex-col gap-20 mt-10">
              {alternatives.map((item, index) => (
                <div className="w-full flex items-center gap-5" key={index}>
                  <div className="w-full flex flex-col gap-4">
                    <div className="font-bold text-2xl text-gray-800">
                      {item.id}. {item.alternative_title}
                    </div>
                    <figure className="relative w-full mb-5 sm:mb-6 flex flex-col gap-2">
                      <Image
                        src={`${BASE_ASSETS_URL}/v2/alternatives/${item.image}`}
                        width="10"
                        height="10"
                        className="w-full h-auto object-cover border"
                        alt={item?.altText}
                      />
                      <figcaption className="text-center px-6 sm:px-10 text-xs sm:text-sm italic">
                        {item?.imageCaption}
                      </figcaption>
                    </figure>
                    <ul className="text-gray-900 flex flex-col gap-4 list-disc text-lg">
                      <ParaGraphs paraList={item.paragraphs} />
                    </ul>

                    <ListComponent
                      title="Key Features"
                      list={item.key_features}
                    />
                    <ListComponent
                      title="Integrations"
                      list={item.integrations}
                    />
                    {item?.pricing && (
                      <ListComponent title="Pricing" list={item.pricing} />
                    )}
                    <ListComponent title="Pros" list={item.pros} />
                    <ListComponent title="Cons" list={item.cons} />
                    {item?.tableData && (
                      <ComparisonTable tableData={item?.tableData} />
                    )}
                    {item?.paragraphs2 && (
                      <ListComponent
                        title={item.paragraphs2?.title}
                        list={item.paragraphs2.paras}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="w-full">
              <div className="w-full flex flex-col gap-6 mt-10">
                {
                  <ParaGraphs
                    heading1={alternativeConclusion.heading1}
                    paraList={alternativeConclusion.paragraphs}
                  />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocusignCompetitors;
